import { Currency, PaymentEvents, Voucher } from '@accedo/one-plugin-sdk';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import { TInputState } from '#/components/Form/Form.types';
import { getErrorMessageKeys } from '#/errors/oneError';
import { getVoucherInputField } from '#/plugins/paywall/components';
import { falsyToNull, noop, SubscriptionParams } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

export interface UseDiscountProps {
  id: string;
  price: { amount: string; currency?: Currency };
  onPriceChange: (price: { amount: string; currency?: Currency }) => void;
  onVoucherChange: (planId: string, voucherCode: string) => Promise<Voucher>;
  onVoucherRemove: () => Promise<void>;
  hasOwnUi: boolean;
  isDisabled?: boolean;
}

export const useDiscount = ({
  id,
  price,
  onPriceChange,
  onVoucherChange,
  onVoucherRemove,
  hasOwnUi,
  isDisabled,
}: UseDiscountProps) => {
  const [inputState, setInputState] = useState<TInputState>({ value: '' });
  const [voucher, setVoucher] = useState<Voucher>();
  const [voucherError, setVoucherError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [applyVoucherFromSearchParams, setApplyVoucherFromSearchParams] =
    useState(false);
  const [dropInLoaded, setDropInLoaded] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const originalPrice = useRef(price);

  const searchParams = new URLSearchParams(window.location.search);
  const searchVoucher = searchParams.get(SubscriptionParams.Voucher);

  const isSubmitButtonDisabled =
    isDisabled ||
    isLoading ||
    !inputState.value ||
    !formRef.current?.checkValidity() ||
    (hasOwnUi && applyVoucherFromSearchParams && !dropInLoaded);

  const isFormInputDisabled =
    isDisabled ||
    isLoading ||
    (hasOwnUi && applyVoucherFromSearchParams && !dropInLoaded);

  useEffect(() => {
    if (!voucher) {
      return;
    }

    onPriceChange({
      amount: falsyToNull(voucher?.discountedPrice) ?? price.amount,
      currency: voucher?.currency ?? price.currency,
    });
  }, [voucher, onPriceChange, price.currency, price.amount]);

  const updateInputState = (_: string, state: TInputState) => {
    setInputState((prevState) => ({ ...prevState, ...state }));
    setVoucherError(undefined);
  };

  const submitVoucherCode = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      setIsLoading(true);

      const inputValue = inputState.value as string;

      onVoucherChange(id, inputValue)
        .then((res) => {
          setVoucher(res);
        })
        .catch((err) => {
          // Resolve a specific error message from the error code, falling back to the
          // generic promo code error for unmapped errors.
          const { keys, context } = getErrorMessageKeys(
            err,
            'iap.voucher.error.message'
          );

          setVoucherError(tb(keys, context));
        })
        .finally(() => setIsLoading(false));
    },
    [id, inputState, onVoucherChange]
  );

  const removeVoucher = useCallback(() => {
    setIsLoading(true);

    onVoucherRemove()
      .then(() => {
        setVoucher(undefined);
        setInputState({ value: '' });
        onPriceChange(originalPrice.current);
      })
      .finally(() => setIsLoading(false));
  }, [onVoucherRemove, onPriceChange]);

  const { key, label, variant } = getVoucherInputField();

  useEffect(() => {
    const onDropInLoaded = () => {
      setDropInLoaded(true);
    };

    if (hasOwnUi && id && searchVoucher) {
      document.addEventListener(PaymentEvents.DropInLoaded, onDropInLoaded);
    }

    return () =>
      document.removeEventListener(PaymentEvents.DropInLoaded, onDropInLoaded);
  }, [hasOwnUi, id, searchVoucher]);

  useEffect(() => {
    if (id && searchVoucher) {
      setInputState({ value: searchVoucher });
      setApplyVoucherFromSearchParams(true);
    }
  }, [id, searchVoucher]);

  useEffect(() => {
    if (applyVoucherFromSearchParams && (!hasOwnUi || dropInLoaded)) {
      setApplyVoucherFromSearchParams(false);
      submitVoucherCode({
        preventDefault: noop,
      } as FormEvent<HTMLFormElement>);
    }
  }, [applyVoucherFromSearchParams, dropInLoaded, hasOwnUi, submitVoucherCode]);

  return {
    inputState,
    voucher,
    voucherError,
    isLoading,
    applyVoucherFromSearchParams,
    dropInLoaded,
    formRef,
    updateInputState,
    submitVoucherCode,
    removeVoucher,
    key,
    label,
    variant,
    isSubmitButtonDisabled,
    isFormInputDisabled,
  };
};
